import { useCallback } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { type MultiLevelMenuItem } from '@/common/types/multi-level-menu.type';
import { type FilteredDataType } from '@/features/store/types/filter.type';
import { useRouter } from 'nextjs-toploader/app';

import { updateQueryParams } from '@/common/utils/updateQueryParams.utility';

export function useFilterTags(filteredData: FilteredDataType) {
  const router = useRouter();
  const vendorPathname = usePathname();
  const searchParams = useSearchParams();
  const queryParams = searchParams.toString();

  const setCategory = useCallback(
    (filterCategories: MultiLevelMenuItem[]) => {
      const newCategoryValue = filterCategories
        .map((item) => item.value)
        .join('-');
      const updatedQuery = updateQueryParams(
        queryParams,
        'category',
        newCategoryValue
      );
      router.replace(`${vendorPathname}${updatedQuery}`);
    },
    [queryParams, router, vendorPathname]
  );

  const handleClick = useCallback(
    (filter: MultiLevelMenuItem) => {
      const isSelected = filteredData.category.some(
        (item) => item.value === filter.value
      );
      const newCategoryList = isSelected
        ? filteredData.category.filter((item) => item.value !== filter.value)
        : [...filteredData.category, filter];
      setCategory(newCategoryList);
    },
    [filteredData.category, setCategory]
  );

  const removeIsDiscountedOrIsAvailable = useCallback(
    (target: keyof FilteredDataType) => {
      const updatedQuery = updateQueryParams(queryParams, target, null);
      router.replace(`${vendorPathname}?${updatedQuery}`);
    },
    [queryParams, router, vendorPathname]
  );

  const removeSortFilter = useCallback(() => {
    const updatedQuery = updateQueryParams(queryParams, 'orderBy', null);
    router.replace(`${vendorPathname}?${updatedQuery}`);
  }, [queryParams, router, vendorPathname]);

  return {
    setCategory,
    handleClick,
    removeIsDiscountedOrIsAvailable,
    removeSortFilter,
  };
}
